import type { NoteOption, NoteOptions } from '@/db/types/case_note';
import { selectedProgram, type CaseNoteFormErrors, type CaseNoteFormState } from '@/lib/case-note-form';
import ServiceUnitsField from './ServiceUnitsField';
import { inputClass, labelClass } from './fields';

interface CaseNoteFormProps {
  state: CaseNoteFormState;
  options: NoteOptions;
  errors: CaseNoteFormErrors;
  onChange: (state: CaseNoteFormState) => void;
}

/** A required-field marker. */
function Required() {
  return <span className="text-red-600"> *</span>;
}

/** A radio group shown as chips: coding, source and check-in. */
function ChoiceChips({ name, label, choices, value, required, error, onChange }: {
  name: string;
  label: string;
  choices: NoteOption[];
  value: string | null;
  required?: boolean;
  error?: string;
  onChange: (value: string | null) => void;
}) {
  return (
    <fieldset>
      <legend className={labelClass}>{label}{required && <Required />}</legend>
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={label}>
        {choices.map(choice => {
          const checked = value === choice.value;
          return (
            <label
              key={choice.value}
              className={`cursor-pointer rounded-full border px-3 py-1.5 text-sm transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-deep-sea-green ${
                checked ? 'border-deep-sea-green bg-[#E6F0F0] text-deep-sea-green font-semibold' : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              }`}
            >
              <input
                type="radio"
                name={name}
                value={choice.value}
                checked={checked}
                onChange={() => onChange(choice.value)}
                onClick={() => { if (checked && !required) onChange(null); }}
                className="sr-only"
              />
              {choice.label}
            </label>
          );
        })}
      </div>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </fieldset>
  );
}

/** The fields of a case or basic note. Which fields show depends on the program's note settings. */
export default function CaseNoteForm({ state, options, errors, onChange }: CaseNoteFormProps) {
  const set = (patch: Partial<CaseNoteFormState>) => onChange({ ...state, ...patch });
  const program = selectedProgram(state, options);
  const isCaseNote = state.type === 'CaseNote';
  const canChooseType = options.note_types.includes('CaseNote') && options.note_types.includes('BasicNote');
  const subjectMode = isCaseNote ? program?.subject.mode ?? 'none' : 'free_text';

  return (
    <div className="space-y-5">
      {canChooseType && (
        <div className="grid grid-cols-2 rounded-lg border-2 border-deep-sea-green overflow-hidden" role="radiogroup" aria-label="Note type">
          {(['CaseNote', 'BasicNote'] as const).map(type => (
            <button
              key={type}
              type="button"
              role="radio"
              aria-checked={state.type === type}
              onClick={() => set({ type, interactionType: null })}
              className={`py-2 text-sm font-semibold ${state.type === type ? 'bg-deep-sea-green text-white' : 'bg-white text-deep-sea-green'}`}
            >
              {type === 'CaseNote' ? 'Case note' : 'Basic note'}
            </button>
          ))}
        </div>
      )}

      {isCaseNote && (
        <div>
          <label htmlFor="note-program" className={labelClass}>Program<Required /></label>
          <select
            id="note-program"
            value={state.programId ?? ''}
            onChange={e => set({ programId: e.target.value ? Number(e.target.value) : null, noteSubjectId: null, subject: '', units: {} })}
            className={inputClass(Boolean(errors.program))}
          >
            <option value="">Choose a program</option>
            {options.programs.map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
          </select>
          {errors.program && <p className="mt-1 text-xs text-red-600">{errors.program}</p>}
        </div>
      )}

      {isCaseNote && options.sites.length > 0 && (
        <div>
          <label htmlFor="note-site" className={labelClass}>Site{options.requires_site && <Required />}</label>
          <select
            id="note-site"
            value={state.siteId ?? ''}
            onChange={e => set({ siteId: e.target.value ? Number(e.target.value) : null })}
            className={inputClass(Boolean(errors.site))}
          >
            <option value="">Choose a site</option>
            {options.sites.map(site => <option key={site.id} value={site.id}>{site.name}</option>)}
          </select>
          {state.siteId != null && state.siteId === options.default_site_id && <p className="mt-1 text-xs text-gray-500">Your primary site</p>}
          {errors.site && <p className="mt-1 text-xs text-red-600">{errors.site}</p>}
        </div>
      )}

      <div>
        <label htmlFor="note-date" className={labelClass}>Date of interaction<Required /></label>
        <input
          id="note-date"
          type="date"
          value={state.interactionDate}
          onChange={e => set({ interactionDate: e.target.value })}
          className={inputClass(Boolean(errors.interactionDate))}
        />
        {errors.interactionDate && <p className="mt-1 text-xs text-red-600">{errors.interactionDate}</p>}
      </div>

      {isCaseNote && program?.uses_coding && (
        <ChoiceChips
          name="note-coding"
          label="Coding"
          required
          choices={options.coding_options}
          value={state.interactionType}
          error={errors.interactionType}
          onChange={interactionType => set({ interactionType })}
        />
      )}

      {!isCaseNote && (
        <ChoiceChips
          name="note-source"
          label="Source"
          required
          choices={options.source_options}
          value={state.interactionType}
          error={errors.interactionType}
          onChange={interactionType => set({ interactionType })}
        />
      )}

      {subjectMode === 'preset' && program && (
        <div>
          <label htmlFor="note-subject" className={labelClass}>Subject<Required /></label>
          <select
            id="note-subject"
            value={state.noteSubjectId ?? ''}
            onChange={e => {
              const subject = program.subject.options.find(option => option.id === Number(e.target.value));
              set({ noteSubjectId: subject?.id ?? null, subject: subject?.name ?? '' });
            }}
            className={inputClass(Boolean(errors.subject))}
          >
            <option value="">Choose a subject</option>
            {program.subject.options.map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
          </select>
          {errors.subject && <p className="mt-1 text-xs text-red-600">{errors.subject}</p>}
        </div>
      )}

      {(subjectMode === 'free_text' || subjectMode === 'preset_or_free_text') && (
        <div>
          <label htmlFor="note-subject" className={labelClass}>Subject{isCaseNote && <Required />}</label>
          <input
            id="note-subject"
            type="text"
            list={subjectMode === 'preset_or_free_text' ? 'note-subject-options' : undefined}
            value={state.subject}
            onChange={e => set({ subject: e.target.value, noteSubjectId: null })}
            className={inputClass(Boolean(errors.subject))}
          />
          {subjectMode === 'preset_or_free_text' && program && (
            <datalist id="note-subject-options">
              {program.subject.options.map(option => <option key={option.id} value={option.name} />)}
            </datalist>
          )}
          {errors.subject && <p className="mt-1 text-xs text-red-600">{errors.subject}</p>}
        </div>
      )}

      {isCaseNote && program?.uses_check_in && (
        <ChoiceChips
          name="note-check-in"
          label="Check-in"
          choices={options.check_in_options}
          value={state.checkIn}
          onChange={checkIn => set({ checkIn })}
        />
      )}

      <div>
        <label htmlFor="note-detail" className={labelClass}>Note<Required /></label>
        <textarea
          id="note-detail"
          rows={6}
          value={state.detail}
          onChange={e => set({ detail: e.target.value })}
          className={inputClass(Boolean(errors.detail))}
        />
        {errors.detail && <p className="mt-1 text-xs text-red-600">{errors.detail}</p>}
      </div>

      {isCaseNote && program && (
        <ServiceUnitsField
          definitions={program.service_definitions}
          units={state.units}
          onChange={units => set({ units })}
          error={errors.units}
        />
      )}
    </div>
  );
}
